import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import {
  CheckCircleFilled,
  CheckOutlined,
  LoadingOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { getPatientPage } from "../../../constants/uiPaths";
import { maskResidentName } from "../../../util/common";
import { FLAG_REVIEWED_EVENT } from "../../copilotEvents";
import { reviewFlag, type FlagPatientPanel } from "../flagChatApi";
import styles from "./styles.module.scss";

/** Compact stand-in for the Copilot page's patient panel inside the narrow
 *  side panel: who the flag is about, and the two actions that matter. */
const FlagStrip = ({ panel }: { panel: FlagPatientPanel }) => {
  const navigate = useNavigate();
  const [reviewed, setReviewed] = useState(!!panel.reviewed);
  const [reviewing, setReviewing] = useState(false);

  const markReviewed = async () => {
    if (reviewing || reviewed) return;
    setReviewing(true);
    try {
      const result = await reviewFlag({
        clinicId: panel.clinic_id,
        alertId: String(panel.alert_id),
        flagKind: panel.flag_kind,
      });
      if (result.ok) {
        setReviewed(true);
        message.success("Flag marked as reviewed.");
        window.dispatchEvent(
          new CustomEvent(FLAG_REVIEWED_EVENT, {
            detail: { alertId: String(panel.alert_id) },
          })
        );
      } else {
        message.error(result.message || "Could not mark as reviewed.");
      }
    } catch {
      message.error("Could not mark as reviewed.");
    } finally {
      setReviewing(false);
    }
  };

  return (
    <div className={styles.flagStrip}>
      <div className={styles.flagStripWho}>
        <span className={styles.flagStripName}>
          {maskResidentName(panel.name)}
        </span>
        {panel.room && (
          <span className={styles.flagStripRoom}>
            {/^room\b/i.test(panel.room) ? panel.room : `Room ${panel.room}`}
          </span>
        )}
      </div>
      <div className={styles.flagStripActions}>
        <button
          type="button"
          className={styles.flagStripButton}
          onClick={() => navigate(getPatientPage(panel.patient_id))}
        >
          <UserOutlined /> Profile
        </button>
        {reviewed ? (
          <span className={styles.flagStripReviewed}>
            <CheckCircleFilled /> Reviewed
          </span>
        ) : (
          <button
            type="button"
            className={`${styles.flagStripButton} ${styles.flagStripReview}`}
            onClick={() => void markReviewed()}
            disabled={reviewing}
          >
            {reviewing ? <LoadingOutlined /> : <CheckOutlined />} Review
          </button>
        )}
      </div>
    </div>
  );
};

export default FlagStrip;
